// SPDX-License-Identifier: AGPL-3.0-or-later

import kitStyles from '@app/features/app/components/dialogs/shared/SettingsKit.module.css';
import {SettingsLinkRow, SettingsRow} from '@app/features/app/components/dialogs/shared/SettingsRow';
import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {SettingsTabContainer, SettingsTabContent} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {MailChatHideSettings} from '@app/features/konvo_mail/components/MailChatHideSettings';
import {MailSignatureEditor} from '@app/features/konvo_mail/components/MailSignatureEditor';
import {MailSwipeSettings} from '@app/features/konvo_mail/components/MailSwipeSettings';
import MailFolderPane from '@app/features/konvo_mail/state/MailFolderPane';
import MailListPreferences, {
	type MailDefaultReply,
	type MailListDensity,
} from '@app/features/konvo_mail/state/MailListPreferences';
import MailSignatures, {
	MAX_MAIL_SIGNATURES,
	type MailSignature,
	type MailSignatureCandidate,
} from '@app/features/konvo_mail/state/MailSignatures';
import {sanitizeSignatureHtml, signatureText} from '@app/features/konvo_mail/utils/MailSignatureImport';
import {Button} from '@app/features/ui/button/Button';
import {Checkbox} from '@app/features/ui/checkbox/Checkbox';
import type {ComboboxOption} from '@app/features/ui/components/form/FormCombobox';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {OutlookLogo} from '@app/features/ui/components/icons/OutlookLogo';
import {Spinner} from '@app/features/ui/components/Spinner';
import {
	ArrowBendDoubleUpLeftIcon,
	ArrowBendUpLeftIcon,
	ArrowClockwiseIcon,
	CaretRightIcon,
	ChatTeardropIcon,
	DownloadSimpleIcon,
	FolderIcon,
	NotePencilIcon,
	PlusIcon,
	RowsIcon,
	SignatureIcon,
	StackSimpleIcon,
	XIcon,
} from '@app/features/ui/icons/Icons';
import {Segmented} from '@app/features/ui/konvo_controls/KonvoControls';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {CompactComboboxRow} from '@app/features/user/components/modals/tabs/components/CompactComboboxRow';
import styles from '@app/features/user/components/modals/tabs/KonvoMailSettingsTab.module.css';
import UserStore from '@app/features/user/state/Users';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useMemo, useState} from 'react';

const NONE = '__none__';

type ImportPhase = 'idle' | 'scanning' | 'found' | 'empty' | 'error' | 'hidden';

/** Editing target: a saved signature id, 'new', or null for the list. */
type Editing = string | 'new' | null;

function useCandidateName() {
	const {t} = useLingui();
	return (candidate: MailSignatureCandidate): string => {
		switch (candidate.name_kind) {
			case 'full':
				return t`Full`;
			case 'short':
				return t`Short`;
			case 'numbered': {
				const index = candidate.name_index;
				return t`Outlook signature ${index}`;
			}
			default:
				return t`Outlook signature`;
		}
	};
}

const IconAction = ({
	label,
	onClick,
	children,
	dataFlx,
}: {
	label: string;
	onClick: () => void;
	children: React.ReactNode;
	dataFlx: string;
}) => (
	<Tooltip text={label} position="top">
		<button type="button" className={styles.iconButton} onClick={onClick} aria-label={label} data-flx={dataFlx}>
			{children}
		</button>
	</Tooltip>
);

const ImportCard = observer(function ImportCard({
	phase,
	candidates,
	selected,
	onScan,
	onToggle,
	onImport,
	onDismiss,
	importing,
	importFailed,
}: {
	phase: ImportPhase;
	candidates: Array<MailSignatureCandidate>;
	selected: ReadonlySet<string>;
	onScan: () => void;
	onToggle: (key: string, checked: boolean) => void;
	onImport: () => void;
	onDismiss: (() => void) | null;
	importing: boolean;
	importFailed: boolean;
}) {
	const {t, i18n} = useLingui();
	const nameFor = useCandidateName();
	const count = selected.size;
	const total = candidates.length;
	const dates = useMemo(() => {
		const days = new Map<string, Date>();
		for (const candidate of candidates) {
			if (!candidate.last_sent_at) continue;
			const date = new Date(candidate.last_sent_at);
			if (Number.isNaN(date.getTime())) continue;
			days.set(date.toDateString(), date);
		}
		const sorted = Array.from(days.values()).sort((a, b) => a.getTime() - b.getTime());
		const format = new Intl.DateTimeFormat(i18n.locale, {day: 'numeric', month: 'short'});
		const parts = sorted.map((date) => format.format(date));
		return parts.length ? new Intl.ListFormat(i18n.locale, {style: 'long', type: 'conjunction'}).format(parts) : '';
	}, [candidates, i18n.locale]);

	let title: React.ReactNode;
	let subtitle: React.ReactNode;
	if (phase === 'found') {
		title =
			total === 1 ? (
				<Trans>We found your signature in your Outlook sent mail</Trans>
			) : (
				<Trans>We found {total} signatures in your Outlook sent mail</Trans>
			);
		subtitle = dates ? (
			<Trans>From mail you sent on {dates}. Logos and links come across as they are.</Trans>
		) : (
			<Trans>Logos and links come across as they are.</Trans>
		);
	} else if (phase === 'empty') {
		title = <Trans>No signature found in your recent sent mail</Trans>;
		subtitle = <Trans>Send a message from Outlook with your signature, then look again. Or make one below.</Trans>;
	} else if (phase === 'error') {
		title = <Trans>Couldn't look through your sent mail</Trans>;
		subtitle = <Trans>Check that Outlook is connected in Connections, then try again.</Trans>;
	} else if (phase === 'scanning') {
		title = <Trans>Bring in your Outlook signature</Trans>;
		subtitle = <Trans>Looking through your recent sent mail…</Trans>;
	} else {
		title = <Trans>Bring in your Outlook signature</Trans>;
		subtitle = <Trans>Konvo looks for it in your recent sent mail, logo and links included.</Trans>;
	}

	const importLabel = count === 2 && total === 2 ? t`Import both` : count > 1 ? t`Import ${count}` : t`Import`;

	return (
		<div className={styles.importCard} data-flx="user.konvo-mail-settings-tab.import">
			<div className={styles.importHead}>
				<span className={styles.logoTile} aria-hidden="true">
					<OutlookLogo size={22} />
				</span>
				<div className={styles.importText}>
					<span className={styles.importTitle}>{title}</span>
					<span className={styles.importSubtitle}>{subtitle}</span>
				</div>
				<div className={styles.importActions}>
					{phase === 'idle' || phase === 'scanning' ? (
						<Button
							variant="secondary"
							small
							onClick={onScan}
							submitting={phase === 'scanning'}
							data-flx="user.konvo-mail-settings-tab.import.scan"
						>
							<Trans>Look for it</Trans>
						</Button>
					) : null}
					{phase === 'found' ? (
						<Button
							small
							onClick={onImport}
							disabled={count === 0}
							submitting={importing}
							leftIcon={<DownloadSimpleIcon size={16} />}
							data-flx="user.konvo-mail-settings-tab.import.confirm"
						>
							{importLabel}
						</Button>
					) : null}
					{phase === 'empty' || phase === 'error' ? (
						<IconAction label={t`Look again`} onClick={onScan} dataFlx="user.konvo-mail-settings-tab.import.retry">
							<ArrowClockwiseIcon size={18} />
						</IconAction>
					) : null}
					{onDismiss && phase !== 'idle' && phase !== 'scanning' ? (
						<IconAction label={t`Close`} onClick={onDismiss} dataFlx="user.konvo-mail-settings-tab.import.dismiss">
							<XIcon size={18} />
						</IconAction>
					) : null}
				</div>
			</div>

			{phase === 'found' ? (
				<div className={styles.candidates}>
					{candidates.map((candidate) => {
						const already = MailSignatures.hasSignatureLike(candidate.html);
						const checked = !already && selected.has(candidate.key);
						const name = nameFor(candidate);
						return (
							<div
								key={candidate.key}
								className={clsx(styles.candidate, checked && styles.candidateOn, already && styles.candidateDone)}
								data-flx="user.konvo-mail-settings-tab.import.candidate"
							>
								<div className={styles.candidateHead}>
									<Checkbox
										checked={checked}
										disabled={already}
										size="small"
										onChange={(value) => onToggle(candidate.key, value)}
										aria-label={t`Import ${name}`}
									/>
									<span className={styles.candidateName}>{name}</span>
									<span className={styles.candidateMeta}>
										{already ? (
											<Trans>Already added</Trans>
										) : candidate.from_reply ? (
											<Trans>Replies</Trans>
										) : (
											<Trans>New mail</Trans>
										)}
									</span>
								</div>
								<div
									className={styles.candidatePreview}
									// Cleaned by extractSignatureHtml and sanitised again here.
									dangerouslySetInnerHTML={{__html: sanitizeSignatureHtml(candidate.html)}}
								/>
							</div>
						);
					})}
				</div>
			) : null}
			{phase === 'found' && importFailed ? (
				<p className={styles.importError} role="alert">
					<Trans>Couldn't save. Try again.</Trans>
				</p>
			) : null}
		</div>
	);
});

const SignatureRowTitle = ({signature}: {signature: MailSignature}) => {
	const {t} = useLingui();
	return (
		<span className={styles.rowTitle}>
			<span className={styles.rowName}>{signature.name}</span>
			{signature.source === 'outlook' ? (
				<Tooltip text={t`Imported from Outlook`} position="top">
					<span className={styles.outlookBadge} role="img" aria-label={t`Imported from Outlook`}>
						<OutlookLogo size={13} />
					</span>
				</Tooltip>
			) : null}
		</span>
	);
};

const SignaturesSection = observer(function SignaturesSection() {
	const {t} = useLingui();
	const [editing, setEditing] = useState<Editing>(null);
	const [phase, setPhase] = useState<ImportPhase>('hidden');
	const [candidates, setCandidates] = useState<Array<MailSignatureCandidate>>([]);
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [importing, setImporting] = useState(false);
	const [importFailed, setImportFailed] = useState(false);

	useEffect(() => {
		void MailSignatures.load();
	}, []);

	const signatures = MailSignatures.signatures;
	// With no signatures yet the import card leads; afterwards it is the "Import from Outlook" row.
	const cardPhase: ImportPhase =
		phase === 'hidden' && signatures.length === 0 && MailSignatures.loaded ? 'idle' : phase;

	const scan = async () => {
		setPhase('scanning');
		setImportFailed(false);
		try {
			const found = await MailSignatures.discover(UserStore.currentUser?.email ?? null);
			setCandidates(found);
			setSelected(new Set(found.filter((c) => !MailSignatures.hasSignatureLike(c.html)).map((c) => c.key)));
			setPhase(found.length ? 'found' : 'empty');
		} catch {
			setPhase('error');
		}
	};

	const nameFor = useCandidateName();
	const importSelected = async () => {
		const chosen = candidates
			.filter((c) => selected.has(c.key) && !MailSignatures.hasSignatureLike(c.html))
			.map((c) => ({...c, name: nameFor(c)}));
		if (!chosen.length) return;
		setImporting(true);
		setImportFailed(false);
		const ok = await MailSignatures.importCandidates(chosen);
		setImporting(false);
		if (ok) {
			setPhase('hidden');
			setCandidates([]);
		} else {
			setImportFailed(true);
		}
	};

	const defaultOptions = useMemo<ReadonlyArray<ComboboxOption<string>>>(
		() => [{value: NONE, label: t`None`}, ...signatures.map((s) => ({value: s.id, label: s.name}))],
		[signatures, t],
	);

	if (editing !== null) {
		const signature = editing === 'new' ? null : MailSignatures.byId(editing);
		return <MailSignatureEditor key={editing} signature={signature} onClose={() => setEditing(null)} />;
	}

	const usage = (id: string): React.ReactNode => {
		const forNew = MailSignatures.defaultNew === id;
		const forReply = MailSignatures.defaultReply === id;
		if (forNew && forReply) return <Trans>New mail and replies</Trans>;
		if (forNew) return <Trans>New mail</Trans>;
		if (forReply) return <Trans>Replies</Trans>;
		return null;
	};

	const full = signatures.length >= MAX_MAIL_SIGNATURES;

	return (
		<>
			{cardPhase !== 'hidden' ? (
				<ImportCard
					phase={cardPhase}
					candidates={candidates}
					selected={selected}
					onScan={() => void scan()}
					onToggle={(key, checked) =>
						setSelected((previous) => {
							const next = new Set(previous);
							if (checked) next.add(key);
							else next.delete(key);
							return next;
						})
					}
					onImport={() => void importSelected()}
					onDismiss={() => {
						setPhase('hidden');
						setCandidates([]);
					}}
					importing={importing}
					importFailed={importFailed}
				/>
			) : null}

			{!MailSignatures.loaded && MailSignatures.loading ? (
				<div className={styles.loading} data-flx="user.konvo-mail-settings-tab.loading">
					<Spinner size="small" />
				</div>
			) : !MailSignatures.loaded && MailSignatures.loadError ? (
				<div className={styles.loadError} data-flx="user.konvo-mail-settings-tab.load-error">
					<span>
						<Trans>Couldn't load your signatures.</Trans>
					</span>
					<IconAction
						label={t`Try again`}
						onClick={() => void MailSignatures.reload()}
						dataFlx="user.konvo-mail-settings-tab.load-retry"
					>
						<ArrowClockwiseIcon size={18} />
					</IconAction>
				</div>
			) : (
				<div className={clsx(kitStyles.rows, styles.signatureList)} data-flx="user.konvo-mail-settings-tab.signatures">
					{signatures.map((signature) => {
						const used = usage(signature.id);
						return (
							<SettingsLinkRow
								key={signature.id}
								className={styles.signatureRow}
								icon={<SignatureIcon size={20} />}
								title={<SignatureRowTitle signature={signature} />}
								description={<span className={styles.rowPreview}>{signatureText(signature.html)}</span>}
								trailing={
									<>
										{used ? <span className={styles.usage}>{used}</span> : null}
										<CaretRightIcon size={18} weight="bold" />
									</>
								}
								onClick={() => setEditing(signature.id)}
								aria-label={t`Edit ${signature.name}`}
								data-flx="user.konvo-mail-settings-tab.signature-row"
							/>
						);
					})}
					<SettingsLinkRow
						icon={<PlusIcon size={20} />}
						title={<Trans>New signature</Trans>}
						description={full ? <Trans>You can keep up to 10 signatures.</Trans> : undefined}
						trailing={<span />}
						disabled={full}
						onClick={() => setEditing('new')}
						data-flx="user.konvo-mail-settings-tab.new-signature"
					/>
					{cardPhase === 'hidden' ? (
						<SettingsLinkRow
							icon={<OutlookLogo size={20} />}
							title={<Trans>Import from Outlook</Trans>}
							description={<Trans>Look for signatures in your recent sent mail</Trans>}
							trailing={<span />}
							disabled={full}
							onClick={() => void scan()}
							data-flx="user.konvo-mail-settings-tab.import-again"
						/>
					) : null}
				</div>
			)}

			{signatures.length > 0 ? (
				<div className={kitStyles.rows} data-flx="user.konvo-mail-settings-tab.defaults">
					<CompactComboboxRow<string>
						label={<Trans>New messages</Trans>}
						className={styles.compactRow}
						icon={<NotePencilIcon size={20} />}
						value={MailSignatures.defaultNew ?? NONE}
						options={defaultOptions}
						onChange={(value) => void MailSignatures.setDefault('new', value === NONE ? null : value)}
						isSearchable={false}
						controlWidth="small"
						dataFlx="user.konvo-mail-settings-tab.default-new"
					/>
					<CompactComboboxRow<string>
						label={<Trans>Replies and forwards</Trans>}
						className={styles.compactRow}
						icon={<ArrowBendUpLeftIcon size={20} />}
						value={MailSignatures.defaultReply ?? NONE}
						options={defaultOptions}
						onChange={(value) => void MailSignatures.setDefault('reply', value === NONE ? null : value)}
						isSearchable={false}
						controlWidth="small"
						dataFlx="user.konvo-mail-settings-tab.default-reply"
					/>
				</div>
			) : null}
			{MailSignatures.saveError && editing === null ? (
				<p className={clsx(kitStyles.note, styles.saveError)} role="alert">
					<Trans>Couldn't save your signature settings. Try again.</Trans>
				</p>
			) : null}
		</>
	);
});

const MailListSection = observer(() => {
	const {t} = useLingui();
	const options: ReadonlyArray<{value: MailListDensity; children: string}> = [
		{value: 'comfortable', children: t`Comfortable`},
		{value: 'compact', children: t`Compact`},
		{value: 'dense', children: t`Dense`},
	];
	return (
		<SettingsSection id="konvo-mail-list" title={<Trans>Mail list</Trans>}>
			<div className={kitStyles.rows}>
				{/* Density changes the list on a computer only, and its three choices don't fit a phone row. */}
				{MobileLayout.enabled ? null : (
					<SettingsRow
						icon={<RowsIcon size={20} />}
						title={<Trans>Density</Trans>}
						description={
							<Trans>How much room each mail takes in the list on a computer. Dense leaves out the preview line.</Trans>
						}
						control={() => (
							<Segmented
								compact
								value={MailListPreferences.density}
								options={options}
								onChange={MailListPreferences.setDensity}
								data-flx="user.konvo-mail-settings-tab.density"
							/>
						)}
					/>
				)}
				{/* The folder panel is a computer thing: a phone always opens folders in a sheet. */}
				{MobileLayout.enabled ? null : (
					<Switch
						label={t`Keep folders open`}
						description={t`Folders stay beside the list. Off, they slide out from the folder button above the list and go away once you pick one.`}
						value={MailFolderPane.pinned}
						onChange={MailFolderPane.setPinned}
						icon={<FolderIcon size={20} />}
						data-flx="user.konvo-mail-settings-tab.folders"
					/>
				)}
				<Switch
					label={t`Group mail by conversation`}
					description={t`One row per conversation with how many mails it holds. Archive, delete and move act on the whole conversation.`}
					value={MailListPreferences.groupConversations}
					onChange={MailListPreferences.setGroupConversations}
					icon={<StackSimpleIcon size={20} />}
					data-flx="user.konvo-mail-settings-tab.group-conversations"
				/>
				<Switch
					label={t`Show the whole conversation when reading`}
					description={t`Earlier mail in the conversation, your replies too, sits folded above the one you open.`}
					value={MailListPreferences.readAsConversation}
					onChange={MailListPreferences.setReadAsConversation}
					icon={<ChatTeardropIcon size={20} />}
					data-flx="user.konvo-mail-settings-tab.read-as-conversation"
				/>
			</div>
		</SettingsSection>
	);
});

const ReplyingSection = observer(() => {
	const {t} = useLingui();
	const options: ReadonlyArray<{value: MailDefaultReply; children: string}> = [
		{value: 'reply_all', children: t`Reply all`},
		{value: 'reply', children: t`Reply`},
	];
	return (
		<SettingsSection id="konvo-mail-replying" title={<Trans>Replying</Trans>}>
			<div className={kitStyles.rows}>
				<SettingsRow
					icon={<ArrowBendDoubleUpLeftIcon size={20} />}
					title={<Trans>Main reply button</Trans>}
					description={
						<Trans>
							The reply the email reader leads with, on a computer and on your phone. Konvo's drafted replies go out the
							same way. The other one stays a tap away.
						</Trans>
					}
					control={() => (
						<Segmented
							compact
							value={MailListPreferences.defaultReply}
							options={options}
							onChange={MailListPreferences.setDefaultReply}
							data-flx="user.konvo-mail-settings-tab.default-reply-mode"
						/>
					)}
				/>
			</div>
		</SettingsSection>
	);
});

export const KonvoMailSettingsTab = observer(() => {
	return (
		<SettingsTabContainer data-flx="user.konvo-mail-settings-tab.container">
			<SettingsTabContent data-flx="user.konvo-mail-settings-tab.content">
				<MailListSection />
				<ReplyingSection />
				<SettingsSection
					id="konvo-mail-signatures"
					title={<Trans>Signatures</Trans>}
					description={<Trans>Added to the end of mail you write in Konvo.</Trans>}
				>
					<SignaturesSection />
				</SettingsSection>
				<MailSwipeSettings />
				<MailChatHideSettings />
			</SettingsTabContent>
		</SettingsTabContainer>
	);
});

export default KonvoMailSettingsTab;
